<template>
  <Modal
    size="xl"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.CenSchedule.CenShowModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div
        class="h-full w-full"
      >
        <div
          v-if="fetchContractEventAction.is(States.LOADING)"
          class="flex justify-center"
        >
          <AppSpinner class="my-8" />
        </div>

        <div
          v-else-if="fetchContractEventAction.is(States.COMPLETE) && contractEvent"
          class="flex flex-col space-y-4 items-center w-full relative"
        >
          <div
            class="grid grid-cols-[1fr_auto_1fr] w-full items-center"
          >
            <div>
              <template v-if="isPending && project?.unarchived">
                <button
                  type="button"
                  class="btn btn--secondary-alert"
                  :disabled="quickActionBusy"
                  @click="handleDecline"
                >
                  {{ $t('HeadContract.CenSchedule.CenShowModal.button.markAsDeclined') }}
                </button>
              </template>
            </div>

            <span
              v-if="statusKey"
              class="py-2 px-4 rounded-lg font-semibold text-sm"
              :class="ContractEventStatusBadgeClasses[statusKey]"
            >
              {{ $t(`HeadContract.CenSchedule.statusTitles.${statusKey}`) }}
            </span>

            <div
              class="ml-auto"
            >
              <button
                type="button"
                class="btn btn--secondary-blue"
                @click="showDetails = !showDetails"
              >
                {{
                  showDetails
                    ? $t('HeadContract.CenSchedule.CenShowModal.button.viewAttachments')
                    : $t('HeadContract.CenSchedule.CenShowModal.button.viewDetails')
                }}
              </button>
            </div>
          </div>

          <div
            v-if="showDetails"
            class="flex space-x-4 w-full"
          >
            <div
              class="flex flex-col w-1/2 space-y-2"
            >
              <p class="font-semibold text-lg">
                {{ $t('HeadContract.CenSchedule.CenShowModal.contractEventDetails') }}
              </p>

              <form class="flex flex-col space-y-4">
                <label>
                  {{ $t('HeadContract.CenSchedule.CenShowModal.name') }}
                  <span class="font-semibold">{{ contractEvent.code }}</span>

                  <AppInput
                    v-model="contractEventForm.form.name"
                    type="text"
                    name="name"
                    class="form-input"
                    :disabled="!canEdit"
                    :error="contractEventForm.getErrors('name')"
                  />
                </label>

                <label>
                  {{ $t('HeadContract.CenSchedule.cause') }}

                  <textarea
                    v-model="contractEventForm.form.cause"
                    name="cause"
                    rows="3"
                    class="resize-none form-input"
                    :disabled="!canEdit"
                  />

                  <p
                    v-if="contractEventForm.hasErrors('cause')"
                    class="text-sm text-red-600"
                  >
                    {{ contractEventForm.getErrors('cause') }}
                  </p>
                </label>

                <label>
                  {{ $t('HeadContract.CenSchedule.effect') }}

                  <textarea
                    v-model="contractEventForm.form.effect"
                    name="effect"
                    rows="3"
                    class="resize-none form-input"
                    :disabled="!canEdit"
                  />

                  <p
                    v-if="contractEventForm.hasErrors('effect')"
                    class="text-sm text-red-600"
                  >
                    {{ contractEventForm.getErrors('effect') }}
                  </p>
                </label>

                <label>
                  {{ $t('HeadContract.CenSchedule.potentialImpact') }}

                  <select
                    v-model.number="contractEventForm.form.impact"
                    name="impact"
                    class="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-ccm-blue-500 focus:ring-ccm-blue-500"
                    :class="{ 'border-red-500': contractEventForm.getErrors('impact') }"
                    :disabled="!canEdit"
                  >
                    <option :value="ContractEventImpact.low">
                      {{ $t('HeadContract.CenSchedule.AddCenModal.impact.low') }}
                    </option>

                    <option :value="ContractEventImpact.medium">
                      {{ $t('HeadContract.CenSchedule.AddCenModal.impact.medium') }}
                    </option>

                    <option :value="ContractEventImpact.high">
                      {{ $t('HeadContract.CenSchedule.AddCenModal.impact.high') }}
                    </option>
                  </select>

                  <p
                    v-if="contractEventForm.hasErrors('impact')"
                    class="text-sm text-red-600"
                  >
                    {{ contractEventForm.getErrors('impact') }}
                  </p>
                </label>

                <label>
                  {{ $t('HeadContract.CenSchedule.CenShowModal.dateOfOccurrence') }}

                  <AppInput
                    v-model="contractEventForm.form.date_of_occurrence"
                    class="form-input"
                    type="date"
                    name="date_of_occurrence"
                    :disabled="!canEdit"
                    :error="contractEventForm.getErrors('date_of_occurrence')"
                  />
                </label>
              </form>
            </div>

            <div
              class="w-1/2 flex flex-col"
            >
              <p class="font-semibold text-lg">
                {{ $t('HeadContract.CenSchedule.CenShowModal.statusTimeline') }}
              </p>

              <div
                class="mt-2 space-y-1"
              >
                <div
                  class="flex justify-between"
                >
                  <span>{{ $t('HeadContract.CenSchedule.CenShowModal.dateOccurred') }}</span>

                  <span
                    v-if="contractEvent.dateOfOccurrence"
                    class="font-semibold"
                  >{{ contractEvent.dateOfOccurrence.toLocaleString() }}</span>

                  <span
                    v-else
                    class="font-semibold"
                  >—</span>
                </div>

                <div
                  v-if="contractEvent.user"
                  class="flex justify-between"
                >
                  <span>
                    {{ $t('HeadContract.CenSchedule.CenShowModal.raisedBy', { name: contractEvent.user.name }) }}
                  </span>

                  <span class="font-semibold">{{ contractEvent.createdAt.toLocaleString() }}</span>
                </div>
              </div>

              <template v-if="canRaise || linkedVariation">
                <hr class="border-gray-300 my-4">

                <div class="space-y-3">
                  <div class="font-semibold flex justify-between">
                    <span>
                      {{ $t('HeadContract.CenSchedule.CenShowModal.variationOrderSection') }}
                    </span>

                    <span class="font-semibold">{{ linkedVariation?.createdAt.toLocaleString() }}</span>
                  </div>

                  <template v-if="linkedVariation">
                    <div class="text-sm flex space-x-2 items-center">
                      <RouterLink
                        v-if="userStore.hasAccessToRoute('VariationsSchedule')"
                        class="btn btn-icon--small btn-icon-blue"
                        :to="{ name: 'VariationsSchedule', params: { projectId: project?.id, variationId: linkedVariation.id } }"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
                      </RouterLink>

                      <span
                        class="font-semibold"
                      >{{ linkedVariation.code }}</span>

                      <p
                        v-if="linkedVariation.description"
                        class="text-sm text-gray-700"
                      >
                        {{ linkedVariation.description }}
                      </p>
                    </div>
                  </template>

                  <button
                    v-else-if="canRaise"
                    type="button"
                    class="btn btn--secondary-blue w-full"
                    :disabled="quickActionBusy"
                    @click="handleRaiseToVariationOrder"
                  >
                    {{ $t('Notifications.contractEventPopup.raiseToVariationOrder') }}
                  </button>
                </div>
              </template>

              <template v-if="canRaise || linkedDelay">
                <hr class="border-gray-300 my-2">

                <div class="space-y-3">
                  <div class="font-semibold flex justify-between">
                    <span>
                      {{ $t('HeadContract.CenSchedule.CenShowModal.delaySection') }}
                    </span>

                    <span class="font-semibold">{{ linkedDelay?.createdAt.toLocaleString() }}</span>
                  </div>

                  <template v-if="linkedDelay">
                    <div class="text-sm flex space-x-2 items-center">
                      <RouterLink
                        v-if="userStore.hasAccessToRoute('DelaysSchedule')"
                        class="btn btn-icon--small btn-icon-blue"
                        :to="{ name: 'DelaysSchedule', params: { projectId: project?.id, delayId: linkedDelay.id } }"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
                      </RouterLink>

                      <span
                        class="font-semibold"
                      >{{ linkedDelay.code }}
                      </span>

                      <p
                        v-if="linkedDelay.description"
                        class="text-sm text-gray-700"
                      >
                        {{ linkedDelay.description }}
                      </p>
                    </div>
                  </template>

                  <button
                    v-else-if="canRaise"
                    type="button"
                    class="btn btn--secondary-blue w-full"
                    :disabled="quickActionBusy"
                    @click="handleRegisterDelay"
                  >
                    {{ $t('Notifications.contractEventPopup.raiseToDelay') }}
                  </button>
                </div>

                <hr class="border-gray-300 mt-2 mb-4">

                <button
                  v-if="canRaise && !linkedVariation && !linkedDelay"
                  type="button"
                  class="btn btn--secondary-blue w-full"
                  :disabled="quickActionBusy"
                  @click="handleRegisterVariationOrderAndDelay"
                >
                  {{ $t('Notifications.contractEventPopup.raiseToVariationOrderAndDelay') }}
                </button>
              </template>
            </div>
          </div>

          <div
            v-else
            class="w-full"
          >
            <AppSpinner
              v-if="contractEventMediaCollection.mediaRequestAction.is(States.LOADING)"
              class="mx-auto my-8"
            />

            <template v-else>
              <div
                v-if="contractEventMediaCollection.mediaCollection.value.length === 0"
                class="font-semibold mb-4 text-center"
              >
                {{ $t('HeadContract.CenSchedule.CenShowModal.noAttachmentsFound') }}
              </div>

              <FileUpload
                v-if="project?.unarchived"
                v-model="fileUploadForm"
                multiple
                class="mb-4"
                @change="fileUploaded"
              />

              <button
                v-if="contractEventMediaCollection.mediaCollection.value.length > 1"
                class="absolute left-0 inset-y-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
                type="button"
                @click="mediaSwiper?.slidePrev()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
              </button>

              <Swiper
                v-if="contractEventMediaCollection.mediaCollection.value.length > 0"
                class="max-w-md mb-10 mx-auto"
                :space-between="20"
                :loop="contractEventMediaCollection.mediaCollection.value.length > 1"
                centered-slides
                :modules="[Pagination]"
                :pagination="{
                  el: '.swiper-pagination-cen-show',
                  type: 'bullets' as const
                }"
                @swiper="setSwiper"
              >
                <SwiperSlide
                  v-for="media in contractEventMediaCollection.mediaCollection.value"
                  :key="media.uuid"
                  class="!h-auto"
                >
                  <div
                    class="flex items-center h-full w-full justify-center"
                  >
                    <MediaDisplay
                      class="max-w-60"
                      :media="media"
                      @delete="contractEventMediaCollection.removeMedia(media.uuid)"
                      @refresh="contractEventMediaCollection.refreshMedia(media.uuid)"
                    />
                  </div>
                </SwiperSlide>
              </Swiper>

              <div
                class="swiper-pagination-cen-show flex justify-center"
              />

              <button
                v-if="contractEventMediaCollection.mediaCollection.value.length > 1"
                class="absolute right-0 inset-y-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
                type="button"
                @click="mediaSwiper?.slideNext()"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
              </button>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-between w-full space-x-4">
        <button
          type="button"
          class="btn btn--tertiary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.CenSchedule.CenShowModal.button.close') }}
        </button>

        <button
          v-if="canEdit"
          type="button"
          class="btn btn--secondary-blue"
          :disabled="updateContractEventAction.is(States.LOADING)"
          @click="updateContractEvent"
        >
          {{ $t('HeadContract.CenSchedule.CenShowModal.button.update') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import MediaDisplay from '@/components/MediaDisplay.vue';
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { type Media, useMediaCollection } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, enumKeyFromValue } from '@/helpers';
import { t } from '@/i18n';
import { ContractEventImpact, ContractEventStatus, ContractEventStatusBadgeClasses } from '@/models/ContractEvent';
import { useContractEventsStore } from '@/store/contractEvents';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import Swal from 'sweetalert2';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperInterface } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  contractEventId: number;
  projectId: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'updated'): void;
}>();

const contractEventsStore = useContractEventsStore();
const projectsStore = useProjectsStore();
const userStore = useUserStore();
const project = computed(() => projectsStore.project);

const contractEvent = computed(() => {
  return contractEventsStore.models.with('user').with('variation_order').with('delay').find(props.contractEventId);
});

const linkedVariation = computed(() => contractEvent.value?.variation_order);
const linkedDelay = computed(() => contractEvent.value?.delay);
const fetchContractEventAction = useStoreApiAction(contractEventsStore.fetchContractEvent);
const updateContractEventAction = useStoreApiAction(contractEventsStore.updateContractEvent);
const showDetails = ref(true);
const contractEventMediaCollection = useMediaCollection(contractEventsStore.media);

const contractEventForm = useFormData({
  name: '',
  cause: '',
  effect: '',
  impact: 0,
  date_of_occurrence: '',
});

watch(contractEvent, (newValue) => {
  if(newValue) {
    contractEventForm.setData(newValue, ['date_of_occurrence']);
    contractEventForm.form.date_of_occurrence = dateForDateInput(newValue.date_of_occurrence ?? new Date());
  }
}, { immediate: true });

watch(
  () => props.contractEventId,
  async (id) => {
    showDetails.value = true;
    contractEventMediaCollection.clear();

    fetchContractEventAction.request(id);
  },
  { immediate: true },
);

const isPending = computed(() => contractEvent.value?.status === ContractEventStatus.pending);
const canEdit = computed(() => isPending.value && Boolean(project.value?.unarchived));
const canRaise = computed(() => isPending.value && Boolean(project.value?.unarchived));

const statusKey = computed(() => {
  if(!contractEvent.value) {
    return undefined;
  }

  return enumKeyFromValue(ContractEventStatus, contractEvent.value.status);
});

const mediaSwiper = ref<SwiperInterface>();

const setSwiper = (swiper: SwiperInterface) => {
  mediaSwiper.value = swiper;
};

const storeMediaAction = useStoreApiAction(contractEventsStore.storeMedia);
const fileUploadForm = ref();

const fileUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files ?? []);

  files.forEach((file) => {
    const fileFormData = new FormData();

    fileFormData.append('images[]', file);

    storeMediaAction.request(props.contractEventId, fileFormData).then((medias) => {
      contractEventMediaCollection.addMedia(medias as unknown as Record<string, Media>);
    }).catch((error) => {
      if(error?.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to upload attachments',
          text: error.data?.message,
        });
      }
    });
  });
};

const updateContractEvent = () => {
  updateContractEventAction.request(props.contractEventId, {
    name: contractEventForm.form.name,
    cause: contractEventForm.form.cause,
    effect: contractEventForm.form.effect,
    impact: contractEventForm.form.impact,
    date_of_occurrence: contractEventForm.form.date_of_occurrence || undefined,
  }).then(() => {
    Swal.fire({
      icon: 'success',
      text: t('HeadContract.CenSchedule.CenShowModal.updateSuccess'),
    });

    emit('updated');
  }).catch((error) => {
    contractEventForm.setErrors(error.data);
  });
};

const quickActionBusy = computed(() => {
  return registerVariationOrderAction.is(States.LOADING) || registerDelayAction.is(States.LOADING) ||
    registerVariationOrderAndDelayAction.is(States.LOADING);
});

const registerVariationOrderAction = useStoreApiAction(contractEventsStore.registerVariationOrder);

const handleRaiseToVariationOrder = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: t('HeadContract.CenSchedule.quickActions.confirmRaiseVariationOrderTitle'),
    text: t('HeadContract.CenSchedule.quickActions.confirmRaiseVariationOrderText'),
    showCancelButton: true,
    confirmButtonText: t('HeadContract.CenSchedule.quickActions.confirm'),
    cancelButtonText: t('HeadContract.CenSchedule.quickActions.cancel'),
  });

  if(!result.isConfirmed || !contractEvent.value) {
    return;
  }

  registerVariationOrderAction.request(contractEvent.value.id).then(() => {
    toast.success(t('HeadContract.CenSchedule.quickActions.toastVariationOrder'), { autoClose: 2000 });
  });
};

const registerDelayAction = useStoreApiAction(contractEventsStore.registerDelay);

const handleRegisterDelay = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: t('HeadContract.CenSchedule.quickActions.confirmRaiseDelayTitle'),
    text: t('HeadContract.CenSchedule.quickActions.confirmRaiseDelayText'),
    showCancelButton: true,
    confirmButtonText: t('HeadContract.CenSchedule.quickActions.confirm'),
    cancelButtonText: t('HeadContract.CenSchedule.quickActions.cancel'),
  });

  if(!result.isConfirmed || !contractEvent.value) {
    return;
  }

  registerDelayAction.request(contractEvent.value.id).then(() => {
    toast.success(t('HeadContract.CenSchedule.quickActions.toastDelay'), { autoClose: 2000 });
  });
};

const registerVariationOrderAndDelayAction = useStoreApiAction(contractEventsStore.registerVariationOrderAndDelay);

const handleRegisterVariationOrderAndDelay = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: t('HeadContract.CenSchedule.quickActions.confirmRaiseBothTitle'),
    text: t('HeadContract.CenSchedule.quickActions.confirmRaiseBothText'),
    showCancelButton: true,
    confirmButtonText: t('HeadContract.CenSchedule.quickActions.confirm'),
    cancelButtonText: t('HeadContract.CenSchedule.quickActions.cancel'),
  });

  if(!result.isConfirmed || !contractEvent.value) {
    return;
  }

  registerVariationOrderAndDelayAction.request(contractEvent.value.id).then(() => {
    toast.success(t('HeadContract.CenSchedule.quickActions.toastBoth'), { autoClose: 2000 });
  });
};

const declineAction = useStoreApiAction(contractEventsStore.decline);

const handleDecline = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: t('HeadContract.CenSchedule.quickActions.confirmDeclineTitle'),
    text: t('HeadContract.CenSchedule.quickActions.confirmDeclineText'),
    showCancelButton: true,
    confirmButtonText: t('HeadContract.CenSchedule.quickActions.confirm'),
    cancelButtonText: t('HeadContract.CenSchedule.quickActions.cancel'),
  });

  if(!result.isConfirmed || !contractEvent.value) {
    return;
  }

  declineAction.request(contractEvent.value.id).then(() => {
    toast.success(t('HeadContract.CenSchedule.quickActions.toastDeclined'), { autoClose: 2000 });
  });
};
</script>

<style scoped></style>
